import { t } from "@lingui/macro";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { PackageDetail } from "@/features/Packages/PackageDetail";
import { PackageDetailSkeleton } from "@/features/Packages/PackageDetail/components/PackageDetailSkeleton";
import { PackagesStackNavigatorParamList } from "@/navigators/ProfileStackNavigator/PackagesStackNavigator/components/PackagesStackNavigator";
import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container";
import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { usePackageDisplayName } from "@whoosh/features/src/Packages";

import { PackageDetailScreenQuery } from "./__generated__/PackageDetailScreenQuery.graphql";

export const PackageDetailScreen = () => {
  const route =
    useRoute<RouteProp<PackagesStackNavigatorParamList, "PackageDetail">>();

  const packageId = route.params.packageId;

  const [queryRef, loadQuery] = useQueryLoader<PackageDetailScreenQuery>(
    PackageDetailScreenQueryString
  );

  useEffect(() => {
    if (packageId) {
      loadQuery(
        { packageId, packageType: "STANDARD" },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [loadQuery, packageId]);
  return (
    <Suspense fallback={<PackageDetailSkeleton />}>
      {queryRef ? <MembershipDetailScreenInner queryRef={queryRef} /> : null}
    </Suspense>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<PackageDetailScreenQuery>;
};

const MembershipDetailScreenInner = ({ queryRef }: InnerProps) => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();

  const data = usePreloadedQuery(PackageDetailScreenQueryString, queryRef);

  const onBackPress = () => {
    navigation.goBack();
  };

  const packageName = usePackageDisplayName(data.package?.package);

  return (
    <Box
      bg="surface-primary"
      flex={1}
      style={{ paddingTop: insets.top, paddingBottom: insets.bottom }}
    >
      <Container flex={1} paddingHorizontal={0}>
        <ScreenHeader
          title={packageName}
          subHeading={""}
          leftItem={
            <Button
              variant="secondary"
              accessibilityLabel={t`Back Button`}
              leftIcon={<ArrowLeftIcon />}
              onPress={onBackPress}
              testID="mobile-header-back-button"
            />
          }
          testID="package-screen-header"
        />
        <ScrollView style={{ paddingTop: 16 }}>
          <PackageDetail type="STANDARD" memberPackageRef={data.package} />
        </ScrollView>
      </Container>
    </Box>
  );
};

const PackageDetailScreenQueryString = graphql`
  query PackageDetailScreenQuery($packageId: ID!, $packageType: PackageType!) {
    package: node(id: $packageId) {
      ...PackageDetail_memberPackage
      ... on MemberPackage {
        package {
          ...usePackageDisplayName_package
        }
      }
    }
  }
`;
